// Reads workflow config out of the `hot_workflows` element that schema_table_json now attaches to each
// table's entry in the schema_tables cache in local storage (see getTableConfigs in ./schemaCache).
// Each entry looks like { context, workflow_name, sort_order, columns }, where `columns` is the
// ALLOWLIST of visible column names (view_column_name / columnMetaData `title`) for that workflow,
// as defined in hot_workflows / hot_workflow_visible_columns tables (and passed up through schema_table_json).
// Columns not in the list are hidden. `context` distinguishes where the dropdown appears (AddRecords, Tables, MultiTable):
// 'add' | 'edit' | 'multitable_parent' | 'multitable_child'.
import { getTableConfigs } from './schemaCache';

// Columns injected by the app itself (see columnMetaData.js), not a real DB/view column, and should not be hidden.
const ALWAYS_VISIBLE_COLUMN_TITLES = new Set(['Select', 'Promote to Gold', 'Create Page']);

export default {
  methods: {
    async getHotWorkflows(viewName, context) {
      const tableConfigs = await getTableConfigs([viewName]);
      const tableInfo = tableConfigs[viewName];
      const workflows = (tableInfo && tableInfo.hot_workflows) || [];

      return workflows
        .filter(wf => wf.context === context)
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order);
    },

    // Given a workflow's visible column names, return the 0-based column indexes (matching columnMetaData's order)
    // that should be hidden (i.e. everything NOT in the visible allowlist). New columns added to the view are hidden
    // by default until explicitly added to public.hot_workflow_visible_columns table in the database.
    // columnMetaData defaults to this.columnMetaData -- pass it explicitly for components (like MultiTable.vue)
    // that manage more than one table's column metadata (e.g. this.columnMetaData2 for the child table).
    hiddenColumnIndexesForWorkflow(visibleColumnNames, columnMetaData = this.columnMetaData) {
      const visible = new Set(visibleColumnNames);
      const matched = new Set();
      const hidden = [];

      columnMetaData.forEach((col, index) => {
        const title = col.title.replace(' **', '');
        if (visible.has(title)) {
          matched.add(title);
        } else if (!ALWAYS_VISIBLE_COLUMN_TITLES.has(title)) {
          hidden.push(index);
        }
      });

      const missing = visibleColumnNames.filter(name => !matched.has(name));
      if (missing.length > 0) {
        console.warn(
          `hot_workflows: table "${this.table}" workflow "${this.workflow}" lists column(s) that don't match any current column: ${missing.join(', ')}. Check hot_workflow_visible_columns for a stale or misspelled column_name (must match column_descriptions.view_column_name exactly).`
        );
      }

      return hidden;
    },

    // Given a plain list of column names to hide (blocklist, as opposed to the allowlist above), resolve
    // them to 0-based column indexes matching columnMetaData's order. Names with no matching column
    // are dropped rather than passed through, since an unresolved index would hide the wrong column.
    // columnMetaData defaults to this.columnMetaData -- pass it explicitly for components (like MultiTable.vue)
    // that manage more than one table's column metadata (e.g. this.columnMetaData2 for the child table).
    hiddenColumnIndexesForNames(names, columnMetaData = this.columnMetaData) {
      return names
        .map(name => columnMetaData.findIndex(c => c.title.replace(' **', '') === name))
        .filter(index => index !== -1);
    }
  }
};
